import { yupResolver } from "@hookform/resolvers/yup";
import { makeStyles } from "@mui/styles";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import * as yup from "yup";

import {
  CONTENT_TYPE,
  DEFAULT_ICONS_V2,
  ELEMENT_FORM_STATE,
  ELEMENT_METHOD,
  ELEMENT_STATUS,
  TAB_STATUSES,
} from "@/constants";
import { UploadService } from "@/services";
import { refreshContentElements, refreshCurrentElement, refreshElements, resetCurrentElement } from "@/store/actions";
import { useThunkDispatch } from "@/store/hooks";
import { elementMediaItemsSelector } from "@/store/selectors";
import { addContentElement } from "@/store/v2/contentElements/actions";
import { addElement } from "@/store/v2/elements/actions";
import { getMediaLink } from "@/utils/mediaLink";
import { elementValidations } from "@/utils/validations/element";

import ElementColorField from "../../ElementColorField/ElementColorField";
import ElementFormActions from "../../ElementFormActions/ElementFormActions";
import ElementIconField from "../../ElementIconField/ElementIconField";
import ElementMethod from "../../ElementMethod/ElementMethod";
import ElementPositioningField from "../../ElementPositioningField/ElementPositioningField";
import ElementShowTextField from "../../ElementShowTextField/ElementShowTextField";
import ElementTitleField from "../../ElementTitleField/ElementTitleField";
import AssetsAutocomplete from "../AssetsAutocomplete/AssetsAutocomplete";
import SelectMicrophone from "../SelectMicrophone/SelectMicrophone";
import VoiceMemoRecordBox from "../VoiceMemoRecordBox/VoiceMemoRecordBox";
import VoiceMemoUploadBox from "../VoiceMemoUploadBox/VoiceMemoUploadBox";

const useStyles = makeStyles({
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "32px",
  },
  formContainer: {
    display: "flex",
    gap: "32px",
  },
  formLeftSide: {
    width: "50%",
    display: "flex",
    flexDirection: "column",
    gap: "32px",
  },
  formRightSide: {
    width: "50%",
  },
});

const voiceMemoFormSchema = yup
  .object({
    experienceId: yup.number().optional().nullable(),
    mediaItemId: yup.number().optional().nullable(),
    type: elementValidations.type,
    memoMethod: elementValidations.memoMethod,
    url: elementValidations.url,
    title: elementValidations.title,
    status: elementValidations.status,
    scope: elementValidations.scope,
    shape: elementValidations.shape,
    showText: elementValidations.showText,
    size: elementValidations.size,
    color: elementValidations.color,
    textColor: elementValidations.textColor,
    linkDestination: elementValidations.linkDestination,
    positioning: elementValidations.positioning,
    xFrameOptions: elementValidations.xFrameOptions,
    helperText: elementValidations.helperText,
    fileName: elementValidations.fileName,
    fileSize: elementValidations.fileSize,
    iconLink: elementValidations.iconLink,
    iconName: elementValidations.iconName,
    linkedId: elementValidations.linkedId,
    selectedMediaItem: yup.object().optional().nullable(),
    microphone: yup.object().optional().nullable(),
    micPermission: yup.string().optional(),
  })
  .required();

export default function VoiceMemoForm(props) {
  const { propertyId, experienceId, mediaItemId, tabStatus, element, onUpdate } = props;

  const classes = useStyles();
  const dispatch = useThunkDispatch();
  const timer = useRef(null);

  const { elementMediaItems } = useSelector(elementMediaItemsSelector(propertyId));
  const { [CONTENT_TYPE.AUDIO]: mediaItems = [] } = elementMediaItems;

  const [microphones, setMicrophones] = useState([]);
  const [selectedFile, setSelectedFile] = useState(element.selectedFile || null);
  const [elementFormState, setElementFormState] = useState(ELEMENT_FORM_STATE.DEFAULT);

  const {
    setValue,
    watch,
    control,
    trigger,
    reset,
    formState: { errors, isDirty },
  } = useForm({
    mode: "all",
    resolver: yupResolver(voiceMemoFormSchema),
    defaultValues: {
      experienceId: element.experienceId || null,
      mediaItemId: element.mediaItemId || null,
      type: element.type,
      memoMethod: element.memoMethod,
      url: element.url || "",
      title: element.title,
      status: element.status,
      scope: element.scope,
      shape: element.shape,
      showText: element.showText,
      size: element.size,
      color: element.color,
      textColor: element.textColor,
      linkDestination: element.linkDestination,
      positioning: element.positioning,
      xFrameOptions: element.xFrameOptions,
      helperText: element.helperText || "",
      fileName: element.fileName,
      fileSize: element.fileSize,
      iconName: element.iconName || "voice-memo",
      iconLink: element.iconLink || DEFAULT_ICONS_V2["voice-memo"],
      linkedId: element.linkedId,
      micPermission: "",
      microphone: null,
    },
    context: {},
  });

  useEffect(() => {
    if (element.id) {
      setValue("experienceId", element.experienceId || null);
      setValue("mediaItemId", element.mediaItemId || null);
      setValue("type", element.type);
      setValue("memoMethod", element.memoMethod);
      setValue("url", element.url || "");
      setValue("title", element.title);
      setValue("status", element.status);
      setValue("scope", element.scope);
      setValue("shape", element.shape);
      setValue("showText", element.showText);
      setValue("size", element.size);
      setValue("color", element.color);
      setValue("textColor", element.textColor);
      setValue("linkDestination", element.linkDestination);
      setValue("positioning", element.positioning);
      setValue("xFrameOptions", element.xFrameOptions);
      setValue("helperText", element.helperText || "");
      setValue("fileName", element.fileName);
      setValue("fileSize", element.fileSize);
      setValue("iconName", element.iconName || "voice-memo");
      setValue("iconLink", element.iconLink || DEFAULT_ICONS_V2["voice-memo"]);
      setValue("linkedId", element.linkedId);
      setSelectedFile(element.selectedFile || null);

      if (selectedFile) {
        setValue("url", URL.createObjectURL(selectedFile), { shouldDirty: true });
      }
    }
  }, [element.id]);

  useEffect(() => {
    if (!selectedFile) return;

    const getElementUrl = async () => {
      try {
        const { url, fileName, fileSize } = await uploadFile();
        setValue("url", url, { shouldDirty: true });
        setValue("fileName", fileName, { shouldDirty: true });
        setValue("fileSize", fileSize, { shouldDirty: true });
      } catch (error) {
        console.error("Error uploading file:", error);
        toast.error("Failed to upload file. Please try again.");
      }
    };

    getElementUrl();
  }, [selectedFile]);

  const formValues = watch();

  const { micPermission } = formValues;
  const disabled = micPermission !== "granted";

  const [previewUrl, setPreviewUrl] = useState(getMediaLink({ link: formValues.url }) || "");

  useEffect(() => {
    if (timer.current) {
      clearTimeout(timer.current);
    }

    timer.current = setTimeout(async () => {
      dispatch(refreshCurrentElement(formValues));

      if (element.id) {
        if (element.experienceId) {
          dispatch(refreshElements(propertyId, element.experienceId, { ...element, ...formValues }));
        } else {
          dispatch(refreshContentElements(propertyId, element.mediaItemId, { ...element, ...formValues }));
        }
      }

      await trigger();
    }, 500);
  }, [...Object.values(formValues)]);

  useEffect(() => {
    if ((selectedFile || element.url) && elementFormState !== ELEMENT_FORM_STATE.REPLACE) {
      setElementFormState(ELEMENT_FORM_STATE.PREVIEW);
    }
  }, []);

  const uploadFile = async () => {
    const fileName = selectedFile.name.split(".").slice(0, -1).join(".").replaceAll(" ", "_");
    const fileSize = selectedFile.size;

    const { resp } = await UploadService.uploadElementFile(propertyId, selectedFile);

    return {
      url: resp.url,
      fileName,
      fileSize,
    };
  };

  const onCreate = async (done) => {
    try {
      const values = {
        ...formValues,
        status: ELEMENT_STATUS.CREATED,
      };

      if ([ELEMENT_METHOD.UPLOAD, ELEMENT_METHOD.RECORD].includes(formValues.memoMethod) && selectedFile) {
        const { url, fileName, fileSize } = await uploadFile();
        values.url = url;
        values.fileName = fileName;
        values.fileSize = fileSize;
      }

      if (tabStatus === TAB_STATUSES.HOMEBASE) {
        await dispatch(addContentElement(propertyId, mediaItemId, values));
      }

      if (tabStatus === TAB_STATUSES.CUSTOMIZE) {
        await dispatch(addElement(propertyId, experienceId, values));
      }

      setSelectedFile(null);

      toast.success(`${formValues.title || element.title} added successfully`, {
        containerId: "dark",
      });

      dispatch(resetCurrentElement());
    } catch {
      toast.error("Oops! Something went wrong...", {
        containerId: "dark",
      });
    } finally {
      if (done) {
        done();
      }
      if (timer.current) {
        clearTimeout(timer.current);
      }
    }
  };

  return (
    <>
      {!formValues.memoMethod && <ElementMethod setValue={setValue} type={element.type} />}
      {!!formValues.memoMethod && (
        <div className={classes.container}>
          {formValues.memoMethod === ELEMENT_METHOD.UPLOAD && (
            <VoiceMemoUploadBox
              element={element}
              formValues={formValues}
              selectedFile={selectedFile}
              setSelectedFile={setSelectedFile}
              elementFormState={elementFormState}
              setElementFormState={setElementFormState}
              previewUrl={previewUrl}
              setPreviewUrl={setPreviewUrl}
            />
          )}
          {formValues.memoMethod === ELEMENT_METHOD.RECORD && (
            <VoiceMemoRecordBox
              element={element}
              formValues={formValues}
              setValue={setValue}
              selectedFile={selectedFile}
              setSelectedFile={setSelectedFile}
              elementFormState={elementFormState}
              setElementFormState={setElementFormState}
              microphones={microphones}
              setMicrophones={setMicrophones}
              disabled={disabled}
              previewUrl={previewUrl}
              setPreviewUrl={setPreviewUrl}
            />
          )}
          {formValues.memoMethod === ELEMENT_METHOD.FROM_LIBRARY && (
            <AssetsAutocomplete
              element={element}
              control={control}
              mediaItems={mediaItems}
              formValues={formValues}
              setValue={setValue}
              previewUrl={previewUrl}
              setPreviewUrl={setPreviewUrl}
              elementFormState={elementFormState}
              setElementFormState={setElementFormState}
            />
          )}
          <div className={classes.formContainer}>
            <div className={classes.formLeftSide}>
              {formValues.memoMethod === ELEMENT_METHOD.RECORD &&
                !formValues.url &&
                !selectedFile &&
                microphones.length && (
                  <SelectMicrophone
                    control={control}
                    formValues={formValues}
                    setValue={setValue}
                    microphones={microphones}
                    disabled={disabled}
                    variant="standard"
                  />
                )}
              {([ELEMENT_METHOD.UPLOAD, ELEMENT_METHOD.FROM_LIBRARY].includes(formValues.memoMethod) ||
                (formValues.memoMethod === ELEMENT_METHOD.RECORD && (formValues.url || selectedFile))) && (
                <ElementTitleField control={control} type={element.type} />
              )}
              <ElementShowTextField control={control} setValue={setValue} formValues={formValues} type={element.type} />
              {tabStatus !== TAB_STATUSES.HOMEBASE && (
                <ElementPositioningField
                  control={control}
                  setValue={setValue}
                  formValues={formValues}
                  type={element.type}
                />
              )}
            </div>
            <div className={classes.formRightSide}>
              <ElementIconField propertyId={propertyId} formValues={formValues} setValue={setValue} />
              <ElementColorField formValues={formValues} setValue={setValue} />
            </div>
          </div>
          <ElementFormActions
            element={element}
            formValues={formValues}
            onCreate={onCreate}
            onUpdate={onUpdate}
            disabled={!!Object.keys(errors).length}
            isDirty={isDirty}
            reset={reset}
          />
        </div>
      )}
    </>
  );
}
